import { useEffect } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AnalyticsRange,
  CvViewingPrivacy,
  PublicProfileAnalyticsActorKind,
  PublicProfileAnalyticsRange,
} from '@tabbio/types';
import { QUERY_KEYS } from '../lib/query-keys';
import {
  fetchCompanyAnalyticsOverview,
  fetchCompanyJobAnalytics,
  fetchCompanyTalentAnalytics,
  fetchUserAnalyticsOverview,
} from '../lib/analytics-api';
import { useAppAuthState } from '../lib/app-auth';
import {
  fetchCvViewingPrivacy,
  fetchPublicProfileAnalytics,
  AnalyticsActivityGoneError,
  fetchPublicProfileAnalyticsActor,
  updateCvViewingPrivacy,
} from '../lib/public-profiles-api';
import { useWorkspace } from '../providers/useWorkspace';

export function useUserAnalyticsOverview(range: AnalyticsRange) {
  return useQuery({
    queryKey: QUERY_KEYS.USER_ANALYTICS_OVERVIEW(range),
    queryFn: () => fetchUserAnalyticsOverview(range),
    staleTime: 30_000,
  });
}

/**
 * Public-profile/CV reach views for the seeker: backs the CV views
 * modal + screen. Includes profile, public CV, and private share-link dimensions.
 *
 * Aggregate view count is available on Free. The API evaluates the detailed
 * analytics entitlement and returns a count-only, server-redacted summary when
 * it is denied, so every consumer can safely request this owned metric.
 */
export function usePublicProfileAnalytics(
  range: PublicProfileAnalyticsRange,
  enabled = true,
  cvId: string | null = null,
) {
  const { user } = useAppAuthState();
  const userId = user?.id ?? 'anonymous';
  return useQuery({
    queryKey: [...QUERY_KEYS.PUBLIC_PROFILE_ANALYTICS(range, cvId), userId],
    queryFn: () => fetchPublicProfileAnalytics({ mode: 'individual', range, cvId }),
    enabled: enabled && Boolean(user?.id),
    staleTime: 30_000,
    // A filter change shows the last answer until the new scope lands, so the
    // totals, chart and feed swap together instead of blanking.
    placeholderData: keepPreviousData,
  });
}

/** One actor's activity for the analytics detail sheet (Premium). */
export function usePublicProfileAnalyticsActor(
  params: {
    range: PublicProfileAnalyticsRange;
    cvId: string | null;
    kind: PublicProfileAnalyticsActorKind;
    id: string | null;
  } | null,
) {
  const { user } = useAppAuthState();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: params
      ? [
          ...QUERY_KEYS.PUBLIC_PROFILE_ANALYTICS_ACTOR(
            params.range,
            params.cvId,
            params.kind,
            params.id,
          ),
          user?.id ?? 'anonymous',
        ]
      : ['public-profile', 'analytics-actor', 'idle'],
    queryFn: () => fetchPublicProfileAnalyticsActor(params!),
    enabled: Boolean(params && user?.id),
    staleTime: 30_000,
    retry: (failures, error) =>
      !(error instanceof AnalyticsActivityGoneError) && failures < 2,
  });
  const gone = query.error instanceof AnalyticsActivityGoneError;
  // The feed that offered this row is out of date: refresh it so the row
  // goes (or reads anonymous) instead of failing again on the next tap.
  useEffect(() => {
    if (gone) {
      void queryClient.invalidateQueries({ queryKey: ['public-profile', 'analytics'] });
    }
  }, [gone, queryClient]);
  return { ...query, gone };
}

/** The member's own "View CVs privately" switch. */
export function useCvViewingPrivacy(enabled = true) {
  const { user } = useAppAuthState();
  return useQuery({
    queryKey: [...QUERY_KEYS.CV_VIEWING_PRIVACY, user?.id ?? 'anonymous'],
    queryFn: fetchCvViewingPrivacy,
    enabled: enabled && Boolean(user?.id),
    staleTime: 60_000,
  });
}

export function useUpdateCvViewingPrivacy() {
  const queryClient = useQueryClient();
  const { user } = useAppAuthState();
  return useMutation({
    mutationFn: (input: CvViewingPrivacy) => updateCvViewingPrivacy(input),
    onSuccess: (data) => {
      queryClient.setQueryData([...QUERY_KEYS.CV_VIEWING_PRIVACY, user?.id ?? 'anonymous'], data);
    },
  });
}

export function useCompanyAnalyticsOverview(
  range: AnalyticsRange,
  enabled = true,
) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.COMPANY_ANALYTICS_OVERVIEW(companyId, range),
    queryFn: () => fetchCompanyAnalyticsOverview(range),
    enabled: Boolean(companyId) && enabled,
    staleTime: 30_000,
  });
}

export function useCompanyTalentAnalytics(
  range: AnalyticsRange,
  enabled = true,
) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.COMPANY_ANALYTICS_TALENT(companyId, range),
    queryFn: () => fetchCompanyTalentAnalytics(range),
    enabled: Boolean(companyId) && enabled,
    staleTime: 30_000,
  });
}

export function useCompanyJobAnalytics(
  jobId: string,
  range: AnalyticsRange,
  enabled = true,
) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.COMPANY_ANALYTICS_JOB(companyId, jobId, range),
    queryFn: () => fetchCompanyJobAnalytics(jobId, range),
    enabled: Boolean(companyId) && enabled && Boolean(jobId),
    staleTime: 30_000,
  });
}
